import Phaser from "phaser";
import {
  ModalLayer,
  NineSlicePanel,
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import { CONTENT_PANEL_STYLE, FONT_FAMILY, PRIMARY_BUTTON_STYLE, TYPO } from "./uiTokens.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import { popupCloseButtonX } from "./directionalUi.js";
import type { RewardLineItem, RewardShape } from "../systems/rewardLineItems.js";
import { drawRewardShapeIcon } from "./rewardShapeIcon.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";

/** 그리드 셀 1개 표시 모델 — t()/formatBig 적용 완료 (GameScene.toRewardCell이 생성). */
export interface RewardCellModel {
  readonly item: RewardLineItem;
  readonly displayName: string;
  readonly amountText: string;
  readonly iconKey: string;
  readonly tint: string;
  /** 모듈 셀일 때 실제 조각 모양 — 있으면 iconKey image 대신 파이프 모양 Graphics로 그린다. */
  readonly shape?: RewardShape;
}

export interface RewardAcquisitionModalOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly cells: RewardCellModel[];
  /** 셀 클릭 — 상세 팝업 표시. */
  readonly onSelectItem: (item: RewardLineItem) => void;
  readonly onClose: () => void;
}

const COLS = 3;
const CELL_W = 80;
const CELL_H = 84;
const CELL_GAP = 6;

/** hex("#rrggbb") → Phaser 색 number. */
function hexColor(hex: string): number {
  return parseInt(hex.replace("#", ""), 16);
}

/**
 * 보상 획득 팝업 — ModalLayer(dim + 중앙 scale tween, depth 2000) 기반. 보상 요소를 3열 그리드
 * (아이콘 + 이름 + 수량)로 표시한다. 셀 클릭 → onSelectItem(상세 팝업). 연타(던전 소탕) 시 setCells로
 * 내용만 갱신(모달 재생성 0). 패널 크기는 첫 구성 시 셀 수로 고정 — 소탕은 동일 인덱스라 셀 수 불변.
 */
export class RewardAcquisitionModal extends ModalLayer {
  private readonly onSelectItem: (item: RewardLineItem) => void;
  private readonly title: Phaser.GameObjects.Text;
  private readonly confirmBtn: NineSliceButton;
  private readonly cx: number;
  /** 그리드 첫 행 셀 중심 y. */
  private readonly gridTopY: number;
  private cellObjects: Phaser.GameObjects.GameObject[] = [];

  constructor(scene: Phaser.Scene, opts: RewardAcquisitionModalOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => opts.onClose(),
    });
    this.onSelectItem = opts.onSelectItem;

    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    this.cx = cx;
    const rows = Math.max(1, Math.ceil(opts.cells.length / COLS));
    const gridH = rows * CELL_H + (rows - 1) * CELL_GAP;
    const pw = 280;
    const ph = 90 + gridH + 60; // 제목 영역(90) + 그리드 + 확인버튼(60)
    const top = cy - ph / 2;
    this.gridTopY = top + 78 + CELL_H / 2;
    // dim 클릭 닫기 — 패널 영역은 제외(패널 내부 클릭은 안 닫힘). 보상은 이미 지급 후 표시라 닫아도 유실 없음.
    this.setBackdropCloseExclusion(cx - pw / 2, top, pw, ph);

    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: pw,
      height: ph,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    this.title = scene.add
      .text(cx, top + 28, t("reward.popup.title"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.accentGold,
      })
      .setOrigin(0.5);
    this.contentContainer.add(this.title);

    const closeBtn = scene.add
      .text(popupCloseButtonX(cx, pw, 18, getTextDirection()), top + 18, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => opts.onClose());
    this.contentContainer.add(closeBtn);

    this.confirmBtn = new NineSliceButton(scene, cx, cy + ph / 2 - 28, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("reward.popup.confirm"),
      widthOverride: 200,
      heightOverride: 36,
    });
    this.confirmBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onClose());
    this.contentContainer.add(this.confirmBtn);

    this.buildCells(opts.cells);
  }

  /** 보상 셀 그리드 (재)구성 — 기존 셀 destroy 후 재배치. */
  private buildCells(cells: RewardCellModel[]): void {
    for (const o of this.cellObjects) o.destroy();
    this.cellObjects = [];
    const totalW = COLS * CELL_W + (COLS - 1) * CELL_GAP;
    const left = this.cx - totalW / 2 + CELL_W / 2; // 첫 열 셀 중심
    cells.forEach((cell, i) => {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const x = left + col * (CELL_W + CELL_GAP);
      const y = this.gridTopY + row * (CELL_H + CELL_GAP);
      const color = hexColor(cell.tint);

      const bg = this.scene.add
        .rectangle(x, y, CELL_W, CELL_H, color, 0.12)
        .setStrokeStyle(1.5, color, 0.9)
        .setInteractive({ useHandCursor: true });
      bg.on("pointerup", () => this.onSelectItem(cell.item));
      // 모듈 셀 + 모양 정보가 있으면 실제 조각 파이프 모양, 아니면 기존 획일 아이콘.
      const icon = cell.shape
        ? drawRewardShapeIcon(this.scene, x, y - 22, 34, cell.shape, color)
        : this.scene.add.image(x, y - 22, cell.iconKey).setDisplaySize(34, 34);
      const name = this.scene.add
        .text(x, y + 8, cell.displayName, {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.tiny,
          color: cell.tint,
          align: "center",
          wordWrap: { width: CELL_W - 8 },
        })
        .setOrigin(0.5);
      const amount = this.scene.add
        .text(x, y + 30, cell.amountText, {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.textBright,
        })
        .setOrigin(0.5);

      this.cellObjects.push(bg, icon, name, amount);
      this.contentContainer.add([bg, icon, name, amount]);
    });
  }

  /** 연타 구매 시 내용만 갱신 (모달 유지 중 호출). */
  setCells(cells: RewardCellModel[]): void {
    this.buildCells(cells);
  }

  /** 언어 변경 — 정적 라벨(제목/확인). 셀 라벨은 setCells 재호출로 갱신. */
  applyLanguage(): void {
    this.title.setText(t("reward.popup.title"));
    this.confirmBtn.setLabel(t("reward.popup.confirm"));
  }
}
